import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CatalogFeature } from "@/types/catalog-feature";
import { useUpdateFeature } from "../hooks/useFeatures";

function unknownToNumberInputString(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) {
      return String(n);
    }
  }
  return "";
}

function parseFiniteNumber(raw: string, onInvalid: () => void): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    onInvalid();
    return null;
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) {
    onInvalid();
    return null;
  }
  return n;
}

type FeatureEditDialogProps = {
  feature: CatalogFeature | null;
  onClose: () => void;
};

export const FeatureEditDialog: React.FC<FeatureEditDialogProps> = ({
  feature,
  onClose,
}) => {
  const { t } = useTranslation();
  const { mutateAsync, isPending } = useUpdateFeature();
  const [limitInput, setLimitInput] = useState("");
  const [unitPriceInput, setUnitPriceInput] = useState("");

  useEffect(() => {
    if (feature) {
      setLimitInput(unknownToNumberInputString(feature.limit));
      setUnitPriceInput(unknownToNumberInputString(feature.unitPrice));
    }
  }, [feature]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feature) {
      return;
    }

    const isNumberType = feature.type === "NUMBER";

    try {
      if (isNumberType) {
        const limit = parseFiniteNumber(limitInput, () =>
          toast.error(t("subscriptions.features.edit.invalidNumberLimit")),
        );
        if (limit === null) {
          return;
        }
        const unitPrice = parseFiniteNumber(unitPriceInput, () =>
          toast.error(t("subscriptions.features.edit.invalidNumberUnitPrice")),
        );
        if (unitPrice === null) {
          return;
        }
        await mutateAsync({
          id: feature.id,
          dto: { limit, unitPrice },
        });
      } else {
        const unitPrice = parseFiniteNumber(unitPriceInput, () =>
          toast.error(t("subscriptions.features.edit.invalidNumberUnitPrice")),
        );
        if (unitPrice === null) {
          return;
        }
        await mutateAsync({
          id: feature.id,
          dto: { unitPrice },
        });
      }
      toast.success(t("subscriptions.features.edit.successToast"));
      onClose();
    } catch {
      toast.error(t("subscriptions.features.edit.errorToast"));
    }
  };

  const showLimit = feature?.type === "NUMBER";

  return (
    <Dialog open={!!feature} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("subscriptions.features.edit.title")}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {showLimit ? (
            <Input
              type="number"
              inputMode="decimal"
              step="any"
              label={t("subscriptions.features.edit.limit")}
              value={limitInput}
              onChange={(e) => setLimitInput(e.target.value)}
            />
          ) : null}

          <Input
            type="number"
            inputMode="decimal"
            step="any"
            label={t("subscriptions.features.edit.unitPrice")}
            value={unitPriceInput}
            onChange={(e) => setUnitPriceInput(e.target.value)}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={onClose}
            >
              {t("subscriptions.features.edit.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {t("subscriptions.features.edit.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
